import type { ResultsWidgetRuntimeState } from './ResultsSelfOwnedModel';
import { Duration } from '@js-joda/core';
import { computed, createModel, signal, untracked } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal, Signal } from '@preact/signals-react';
import type {
    ColumnOptions,
    OutputColumnWithMetadata,
    SortModel,
} from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import type { IdeaDTO, IdeaPlanDTO } from 'src/dtos/Idea';
import { searchWithColumnFilters } from 'src/features/table/columnFilters/columnFilterFormula';
import { buildResultsTableQueryOptions } from 'src/features/table/hooks/useResultsTableQueryOptions';
import type { TableCreationQuery, TableModel } from 'src/models/TableModel';
import { makeColumnTemplateEqualityKey } from 'src/types';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import { v4 } from 'uuid';

export interface ResultsTableModelInit {
    table: TableModel;
    logger: Logger;
    devtoolsName: string;
    /** Input readiness from the self-owned model; the query stays gated until `ready`. */
    inputState: ReadonlySignal<ResultsWidgetRuntimeState>;
    /**
     * The template the server table is built from; the query stays gated while it is null. The
     * root wires the column template here, or a stitching stage's table template (ADR 0020).
     */
    columnTemplate: ReadonlySignal<Pick<ColumnTemplate, 'columns'> | null>;
    fastIdeas: ReadonlySignal<IdeaDTO<IdeaPlanDTO>[]>;
    slowIdea: ReadonlySignal<IdeaDTO<IdeaPlanDTO> | null>;
    fastIdeasCacheKey: ReadonlySignal<string>;
    slowIdeaCacheKey: ReadonlySignal<string>;
    /** The sort the server table applies; keep an equal sort's reference so the query is stable. */
    sort: ReadonlySignal<SortModel | undefined>;
    scrolling: ReadonlySignal<boolean>;
    stickyFilterValue: ReadonlySignal<string>;
    /** Session column filters as a formula; while defined, the search is sent as a formula too. */
    columnFilterFormula: ReadonlySignal<string | undefined>;
    /** The string columns a search matches when it is sent as a formula. */
    searchColumnIds: ReadonlySignal<Iterable<string>>;
    columnOptions: ReadonlySignal<Partial<ColumnOptions>>;
    validationModeEnabled: ReadonlySignal<boolean>;
    dwellTimeSeconds: ReadonlySignal<number>;
    userViewKey: ReadonlySignal<string>;
}

export interface ResultsTableModelShape {
    /** Combined runtime state: input resolution plus table-run failures. */
    readonly state: ReadonlySignal<ResultsWidgetRuntimeState>;
    /** Validation columns returned by table-creation metadata. */
    readonly validationColumns: ReadonlySignal<OutputColumnWithMetadata[]>;
    /**
     * The query for a one-off table from today's definition with every column of
     * `columnTemplate`, under a fresh `userViewKey`. A stitched export snapshots it (ADR 0020,
     * OD-EXPORT-001). Null until every input is resolved.
     */
    fullTableQuery(columnTemplate: Pick<ColumnTemplate, 'columns'>): TableCreationQuery | null;
}

/**
 * Owns the Results table query lifecycle: it initializes the injected
 * TableModel with reactive query options that stay disabled until every
 * input (column template, fast/slow idea DTOs, initial sort) is resolved.
 * The first enabled query is therefore also the final one for the loaded
 * state — one table creation, no template-resolved-late or
 * ideas-arrived-late recreations.
 */
export const ResultsTableModel: ModelConstructor<ResultsTableModelShape, [ResultsTableModelInit]> = createModel<
    ResultsTableModelShape,
    [ResultsTableModelInit]
>((init: ResultsTableModelInit) => {
    const validationColumns: Signal<OutputColumnWithMetadata[]> = signal([]);

    const columnTemplateRunKey = computed(() => makeColumnTemplateEqualityKey(init.columnTemplate.value ?? undefined));

    const tableQueryOptions = computed(() => {
        const inputState = init.inputState.value;
        const columnTemplate = init.columnTemplate.value;

        if (inputState.type !== 'ready' || !columnTemplate) {
            return {
                queryKey: ['results-widget-table', 'initializing'] as const,
                queryFn: async () => [null, []] as [null, OutputColumnWithMetadata[]],
                enabled: false,
                staleTime: 0,
                gcTime: 0,
            };
        }

        return buildQueryOptions(columnTemplate, columnTemplateRunKey.value, init.userViewKey.value);
    });

    // Reads every input but the template and the view key, which the live table and an export's
    // full table supply separately.
    function buildQueryOptions(
        columnTemplate: Pick<ColumnTemplate, 'columns'>,
        runKey: string | undefined,
        userViewKey: string,
    ): ReturnType<typeof buildResultsTableQueryOptions> {
        const filter = searchWithColumnFilters(
            init.stickyFilterValue.value,
            init.columnFilterFormula.value,
            init.searchColumnIds.value,
        );
        const scrolling = init.scrolling.value;
        const dwellSeconds = init.dwellTimeSeconds.value;
        const fastIdeaDwellTime = scrolling && dwellSeconds > 0 ? Duration.ofSeconds(dwellSeconds) : undefined;

        return buildResultsTableQueryOptions({
            slowIdea: init.slowIdea.value ?? undefined,
            fastIdeas: init.fastIdeas.value,
            fastIdeaDwellTime,
            validationModeEnabled: init.validationModeEnabled.value,
            columnTemplate,
            useScrolling: scrolling,
            filter,
            userViewKey,
            ideaUserViewKeyScope: 'none',
            columnOptions: init.columnOptions.value,
            log: init.logger,
            fastIdeasCacheKey: init.fastIdeasCacheKey.value,
            slowIdeaCacheKey: init.slowIdeaCacheKey.value,
            columnTemplateRunKey: runKey,
            sort: init.sort.value,
        });
    }

    const fullTableQuery = (columnTemplate: Pick<ColumnTemplate, 'columns'>): TableCreationQuery | null =>
        untracked(() => {
            if (init.inputState.value.type !== 'ready') {
                return null;
            }
            // A view key of its own: a stable key lets the server amend that view in place, and
            // the live keys table must keep its columns (CN-IMPL-004).
            const options = buildQueryOptions(columnTemplate, makeColumnTemplateEqualityKey(columnTemplate), v4());
            return {
                ...options,
                select: (data: [FetchableTableCreationResultWithColumns | null, OutputColumnWithMetadata[]]) => data[0],
            };
        });

    // Validation columns ride along in the query payload; peel them off so
    // the TableModel only sees the table-creation result.
    const tableOnlyQueryOptions = computed(() => {
        const opts = tableQueryOptions.value;
        return {
            ...opts,
            select: (data: [FetchableTableCreationResultWithColumns | null, OutputColumnWithMetadata[]]) => {
                if (data[1]) {
                    validationColumns.value = data[1];
                }
                return data[0];
            },
        };
    });

    init.table.init({
        queryOptions: tableOnlyQueryOptions,
        initBounds: { firstRow: 0, windowSize: 100 },
        devtoolsName: init.devtoolsName,
    });

    const state: ReadonlySignal<ResultsWidgetRuntimeState> = computed(() => {
        const inputState = init.inputState.value;
        if (inputState.type !== 'ready') {
            return inputState;
        }
        if (init.table.failureCount.value > 0 && init.table.error) {
            return { type: 'error', stage: 'table', error: init.table.error };
        }
        return { type: 'ready' };
    });

    return {
        state,
        validationColumns: computed(() => validationColumns.value),
        fullTableQuery,
    };
});

export type ResultsTableModel = Model<ResultsTableModelShape>;
